import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import startOfDay from 'date-fns/startOfDay'
import { usePageTitle } from '../../../actions/app'
import * as PromosActions from '../../../actions/promos'
import { useAppDispatch } from '../../../actions/types'
import { Button, Centered, Column, Confirmation, Input, Loader, Padding, SearchInput, Table } from '../../../components'
import { colors, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import * as PromosSelectors from '../../../reducers/promos'
import type { Promo } from '../../../types'
import { ArrayUtils } from '../../../utils'
import PromoForm from './PromoForm'
import promoTableDisplay from './Promos.table'
import usePromoParams from './usePromoParams'
import { useSize } from '../../../components/utils'

export type ExtendedPromo = Promo & { selected: boolean }

export default function Promos() {
    const dispatch = useAppDispatch()
    const isWorking = useAppSelector(PromosSelectors.isWorking)
    const isLoadingSelectedPromo = useAppSelector(PromosSelectors.isLoadingSelectedPromo)
    const promos = useAppSelector(PromosSelectors.selectPromos)
    const selectedPromo = useAppSelector(PromosSelectors.selectedPromo)
    const { t } = useTranslation()
    const history = useHistory()
    const { isMobile } = useSize()
    const { estate, promoId } = usePromoParams()

    usePageTitle(promoId && selectedPromo ? selectedPromo.name : t('promos-title'))

    const formRef = React.useRef<HTMLDivElement>(null)

    const [bulkSelectedPromos, setBulkSelectedPromos] = React.useState<number[]>([])
    const [isCreatingNewPromo, setIsCreatingNewPromo] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState('')
    const [showAllPromos, setShowAllPromos] = React.useState(false)
    const today = React.useMemo(() => startOfDay(new Date()), [])

    React.useEffect(() => {
        dispatch(PromosActions.fetchPromos())
    }, [dispatch, estate])

    React.useEffect(() => {
        if (promoId) {
            setIsCreatingNewPromo(false)
            dispatch(PromosActions.fetchPromo(promoId))

            if (isMobile) {
                formRef.current?.scrollIntoView()
            }
        } else if (isMobile) {
            window.scrollTo(0, 0)
        }
    }, [dispatch, estate, promoId, isMobile])

    const filteredPromos = React.useMemo(() => {
        const normalizedTerm = searchTerm.trim().toLowerCase()
        const sortByDateStartAsc = (list: Promo[]) =>
            [...list].sort((a, b) => a.date_start.getTime() - b.date_start.getTime())

        if (normalizedTerm.length >= 3) {
            return sortByDateStartAsc(promos.filter(promo =>
                promo.name.toLowerCase().includes(normalizedTerm),
            ))
        }

        const visiblePromos = showAllPromos
            ? promos
            : promos.filter(promo => {
                const start = startOfDay(promo.date_start)
                const end = startOfDay(promo.date_end)
                const isFuture = start > today
                const isActive = start <= today && end >= today
                return isFuture || isActive
            })

        return sortByDateStartAsc(visiblePromos)
    }, [promos, searchTerm, showAllPromos, today])

    const extendedPromos: ExtendedPromo[] = React.useMemo(() => {
        return filteredPromos.map(promo => {
            return {
                ...promo,
                selected: bulkSelectedPromos.includes(promo.id),
            }
        })
    }, [filteredPromos, bulkSelectedPromos])

    if (isWorking) {
        return (
            <Padding top>
                <Centered horizontal>
                    <Loader />
                </Centered>
            </Padding>
        )
    }
    
    const onClickRow = (clickedPromoId?: number) => {
        if (!clickedPromoId) {
            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_PROMOS, { estate }),
            )
            return
        }

        const path = Routes.buildRoute(
            Routes.SELF_SERVE_PROMOS,
            { estate, promo: clickedPromoId },
        )

        if (isMobile) {
            history.push(path)
        } else {
            history.replace(path)
        }
    }

    const onSelectPromo = (id: number, isSelected: boolean) => {
        setBulkSelectedPromos(prevSelection => {
            const itemIndex = prevSelection.findIndex(selectedId => selectedId === id)
            const isAlreadyIncluded = itemIndex > -1

            if (isSelected && !isAlreadyIncluded) {
                return [ ...prevSelection, id ]
            } else if (!isSelected && isAlreadyIncluded) {
                return ArrayUtils.removeItem(prevSelection, itemIndex)
            }

            return prevSelection
        })
    }

    const onDeletePromos = () => {
        const fn = async () => {
            const isDeletingSelected = !!promoId && bulkSelectedPromos.includes(promoId)

            await Promise.all(bulkSelectedPromos.map(async promoId => {
                await dispatch(PromosActions.deletePromo(promoId))
            }))

            if (isDeletingSelected) {
                history.replace(
                    Routes.buildRoute(Routes.SELF_SERVE_PROMOS, { estate })
                )
                dispatch(PromosActions.fetchPromos())
            } else {
                dispatch(PromosActions.fetchPromos())
            }

            setBulkSelectedPromos([])
        }

        fn()
    }

    const onClickCreate = () => {
        setIsCreatingNewPromo(true)
        onClickRow()

        if (isMobile) {
            formRef.current?.scrollIntoView()
        }
    }

    return (
        <>
            <Padding>
                <Button
                    theme="forestGreen"
                    onClick={onClickCreate}
                >
                    {t('promo-create')}
                </Button>
                <Padding top>
                    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', marginBottom: '10px' }}>
                        <SearchInput
                            width="280px"
                            value={searchTerm}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm.length > 0 && (
                            <button
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: colors.greydark,
                                    padding: 0,
                                    height: '16px',
                                    width: '16px',
                                    lineHeight: '16px',
                                }}
                            >
                                x
                            </button>
                        )}
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '10px' }}>
                        <Input
                            type="check"
                            value={showAllPromos}
                            onChange={() => setShowAllPromos(prev => !prev)}
                        />
                        <span>Show completed promos</span>
                    </label>
                </Padding>
                <Confirmation
                    cancel={t('cancel')}
                    confirmation={t('promo-remove-confirm-body', {
                        count: bulkSelectedPromos.length,
                    })}
                    header={t('promo-remove-confirm-header')}
                    onCancel={() => null}
                    onConfirm={onDeletePromos}
                    ok={t('promo-remove-confirm-button')}
                    theme="danger"
                >
                    <Button
                        theme={bulkSelectedPromos.length > 0 ? 'danger' : 'disabled'}
                        disabled={bulkSelectedPromos.length === 0}
                    >
                        {bulkSelectedPromos.length === 0 && t('promo-remove-count-none')}
                        {bulkSelectedPromos.length > 0 && t('promo-remove-count', { count: bulkSelectedPromos.length })}
                    </Button>
                </Confirmation>
            </Padding>
            <Column width={isMobile ? '100%' : '50%'}>
                <Table
                    data={extendedPromos}
                    display={
                        promoTableDisplay({ onSelectPromo, selectedPromoId: promoId, t })
                    }
                    keyField="id"
                    onClickRow={onClickRow}
                    hideDeviceButtons
                    selectedItem={promoId}
                />
            </Column>
            <Column
                ref={formRef}
                width={isMobile ? '100%' : '50%'}
                color={colors.bluedarkest}
            >
                {!selectedPromo && isLoadingSelectedPromo && (
                    <Padding top>
                        <Centered horizontal>
                            <Loader />
                        </Centered>
                    </Padding>
                )}
                {(isCreatingNewPromo || !selectedPromo) && <PromoForm />}
                {!isCreatingNewPromo && selectedPromo && <PromoForm promo={selectedPromo} />}
            </Column>
        </>
    )
}
